
import { computed, defineComponent, onMounted } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import { ElNotification } from "element-plus";
import { saveFile } from "@/core/services/FileSaverService";
import { useStore } from "vuex";

interface OptionItem {
  value: string;
  label: string;
  type?: string;
}

export default defineComponent({
  name: "InquiryReference",
  setup() {
    const store = useStore();

    onMounted(() => {
      setCurrentPageTitle("Лавлагааны тайлан");
    });

    const isu = computed(() => {
      return store.getters.getMbUser;
    });

    return {
      isu
    };
  },
  data() {
    const currentYear = new Date().getFullYear();
    const currentMonth = (new Date().getMonth() + 1)
      .toString()
      .padStart(2, "0");
    const years: OptionItem[] = [];
    for (let year = currentYear; year >= 2014; year--) {
      years.push({ value: year.toString(), label: year.toString() });
    }
    const months: OptionItem[] = Array.from({ length: 12 }, (_, index) => {
      const value = (index + 1).toString().padStart(2, "0");
      return { value, label: `${index + 1} сар` };
    });

    return {
      page: 1,
      pageSize: 10,
      dataLoading: false,
      loadingReport: "",
      searchValue: "",
      searchTimer: 0,
      yearValue: currentYear.toString(),
      monthValue: currentMonth,
      years,
      months,
      tableData: [] as Array<any>,
      orgList: [] as OptionItem[],
      selectOrgVal: "*",
      orgTypeValue: "*",
      orgTypes: [
        { value: "*", label: "Бүгд" },
        { value: "1", label: "Банк" },
        { value: "2", label: "ББСБ" },
        { value: "3", label: "ХЗХ" },
        { value: "4", label: "Төрийн байгууллага" },
        { value: "5", label: "Үүрэн холбоо" },
        { value: "6", label: "Санхүүгийн түрээс" },
        { value: "7", label: "Даатгалын компани" },
        { value: "8", label: "Үнэт цаасны компани" },
        { value: "9", label: "Бусад" }
      ] as OptionItem[]
    };
  },
  computed: {
    filteredOrgList(): OptionItem[] {
      return this.orgList.filter((data: OptionItem) => {
        return this.orgTypeValue === "*" || data.type === this.orgTypeValue;
      });
    },
    filteredTableData(): Array<any> {
      const search = this.searchValue.toLowerCase();
      return this.tableData.filter((data: any) => {
        return !search || data.searchfield.includes(search);
      });
    },
    paginatedTableData(): Array<any> {
      return this.filteredTableData.slice(
        this.pageSize * this.page - this.pageSize,
        this.pageSize * this.page
      );
    }
  },
  mounted() {
    if (this.isu) {
      this.getOrg();
    }
    this.getInqRef();
  },
  methods: {
    handleCurrentChange(val: number) {
      this.page = val;
    },
    handlePageSizeChange(value: number) {
      this.pageSize = value;
      this.page = 1;
    },
    handleOrgTypeChange() {
      this.selectOrgVal = "*";
      this.refreshReport();
    },
    handleSearchChange() {
      this.page = 1;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => {
        this.getInqRef();
      }, 350);
    },
    refreshReport() {
      this.page = 1;
      this.getInqRef();
    },
    buildParams(username?: string) {
      return {
        year: this.yearValue,
        month: this.monthValue,
        orgType: this.orgTypeValue,
        orgcode: this.selectOrgVal || "*",
        search: this.searchValue,
        username: username || null
      };
    },
    buildQueryString() {
      const params = this.buildParams() as Record<string, string | null>;
      return (
        "?" +
        Object.keys(params)
          .filter(key => params[key] !== null && params[key] !== "")
          .map(
            key =>
              `${encodeURIComponent(key)}=${encodeURIComponent(
                String(params[key])
              )}`
          )
          .join("&")
      );
    },
    async getInqRef() {
      this.dataLoading = true;
      await ApiService.get("inquiryreference", this.buildQueryString())
        .then(res => {
          const results = res.data;
          if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else {
            this.tableData = results.response || [];
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Өгөгдөл уншихад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        })
        .finally(() => {
          this.dataLoading = false;
        });
    },
    async getOrg() {
      await ApiService.get("org")
        .then(resp => {
          const results = resp.data;
          if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else {
            this.orgList = results.response.map(org => ({
              value: org.orgcode,
              type: org.orgtypecode,
              label: `${org.orgcode} - ${org.orgname}`
            }));
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Байгууллагын жагсаалт уншихад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        });
    },
    async getReport(type: string, username?: string) {
      if (this.loadingReport !== "") {
        return;
      }

      document.body.classList.add("waiting");
      this.loadingReport =
        type === "inquiryHistoryByUser" ? `user:${username}` : type;

      const params = {
        ...this.buildParams(username),
        type
      };

      await ApiService.post("inquiryreference", { params })
        .then(resp => {
          const results = resp.data;
          if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error"
            });
          } else if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning"
            });
          } else {
            saveFile(
              results.response,
              this.reportFileName(type, username),
              "application/vnd.ms-excel",
              ".xls"
            );
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Тайлан татахад алдаа гарлаа.",
            type: "error"
          });
        })
        .finally(() => {
          document.body.classList.remove("waiting");
          this.loadingReport = "";
        });
    },
    reportFileName(type: string, username?: string) {
      const period = `${this.yearValue}${
        this.monthValue === "*" ? "" : this.monthValue
      }`;
      if (type === "inquiryHistoryByUser") {
        return `inquiry_history_${username}_${period}`;
      }
      if (type === "inquiryAllUsers") {
        return `inquiry_users_${period}`;
      }
      return `inquiry_total_access_${period}`;
    }
  }
});
